import React, { useContext, useMemo } from 'react'
import './index.scss'
import SearchPanel from '@Components/Search/SearchPanel'
import { StoresContext } from '@App/contexts'
import {
    OVERCLAIM_SEARCH_DEFAULT_FILTERS,
    OverclaimFilterType,
    SEARCH_OVERCLAIM_WORK_OPTIONS,
} from '@App/stores/overclaim/overclaim-search-store'
import Sidebar from '@Components/Sidebars/Sidebar'
import DownloadSidebar from '@Components/Sidebars/DownloadSidebar'
import { SidebarDownloadType } from '@App/enum/sidebar-download-type'
import ModalPortal from '@Components/Popups/ModalPortal'
import OverclaimExportButton from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/components/OverclaimExportButton'
import {
    OverclaimSearchFilters,
    overclaimSortOptions,
} from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/overclaim-search-filter-definition'
import { observer } from 'mobx-react'
import OverclaimSearchHeader from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/components/OverclaimSearchHeader'
import BulkActionLeavePageModal from '@Components/BulkAction/BulkActionLeavePageModal'
import OverclaimSearchResultsSection from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/components/OverclaimSearchResultsSection/OverclaimSearchResultsSection'
import { useRightsholderOverclaimSearch } from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/hooks/useRightsholderOverclaimSearch'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import { OverclaimWorkSearchQuery } from '@App/stores/search/models/overclaim-search'

const RightsholderOverclaimSearch = () => {
    const { overclaimSearchStore, overclaimSearchBulkActionStore } = useContext(StoresContext)
    // A dashboard card (or any other link) can point here with e.g. ?status=OVERCLAIM&documentationStatus=DOCUMENT_NEEDED
    // to land already filtered; the filters stay reflected in the URL as they're changed from here on.
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<OverclaimFilterType>(OverclaimSearchFilters)
    // Likewise for the search terms (e.g. ?title=...), so a bookmarked or shared link re-runs the same search.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(SEARCH_OVERCLAIM_WORK_OPTIONS)
    // A link rarely carries every filter (e.g. no sort), so layer it over the store defaults rather than
    // replacing them — searchForWorks only sorts when a sort is present in the filters.
    const linkedFilters = useMemo(
        () => (urlFilterValues ? { ...OVERCLAIM_SEARCH_DEFAULT_FILTERS, ...urlFilterValues } : undefined),
        [urlFilterValues]
    )
    const {
        criteriaStore,
        filterCount,
        loading,
        runSearch,
        runSearchWithNewCriteria,
        onChangeLimit,
        reset,
        blocker,
        bulkActionLabel,
        exportSidebarIsOpen,
        setExportSidebarIsOpen,
        closeExportSidebar,
    } = useRightsholderOverclaimSearch(linkedFilters, urlCriteria)

    return (
        <div className="rightsholder-overclaim-wrapper">
            <OverclaimSearchHeader statusCounts={filterCount} />
            <section className="overclaim-search-result-section">
                <BulkActionLeavePageModal
                    store={overclaimSearchBulkActionStore}
                    runSearch={runSearch}
                    blocker={blocker}
                    bulkActionLabel={bulkActionLabel}
                />
                <SearchPanel
                    searchCriteriaStore={criteriaStore}
                    onSearch={(searchCriteria: OverclaimWorkSearchQuery, filters: OverclaimFilterType) => {
                        syncCriteriaToUrl(searchCriteria)
                        return runSearchWithNewCriteria(searchCriteria, filters)
                    }}
                    disabled={loading}
                    onReset={() => {
                        syncFiltersToUrl(undefined)
                        syncCriteriaToUrl(undefined)
                        reset()
                    }}
                    defaultFilterValues={linkedFilters ?? OVERCLAIM_SEARCH_DEFAULT_FILTERS}
                    filterFieldCollections={OverclaimSearchFilters}
                    onFilterValueChange={syncFiltersToUrl}
                    sortOptions={overclaimSortOptions}
                    sortFilterKey="sort"
                    extraButtons={[
                        <OverclaimExportButton
                            key="export-button"
                            overclaimSearchStore={overclaimSearchStore}
                            loading={loading}
                            setSidebarIsOpen={setExportSidebarIsOpen}
                        />,
                    ]}
                />
            </section>
            <OverclaimSearchResultsSection loading={loading} onChangeLimit={onChangeLimit} runSearch={runSearch} />
            <ModalPortal isOpen={exportSidebarIsOpen} onClose={closeExportSidebar}>
                <Sidebar onClose={closeExportSidebar}>
                    <DownloadSidebar
                        closeSidebar={closeExportSidebar}
                        sidebarDownloadType={SidebarDownloadType.OverclaimExport}
                    />
                </Sidebar>
            </ModalPortal>
        </div>
    )
}

export default observer(RightsholderOverclaimSearch)
